// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

/**
 * Declarative navigation model. Single source of truth for the sidebar.
 *
 * Each item carries:
 *   key                : stable React key
 *   label              : visible text
 *   icon               : lucide-react icon component
 *   route              : router path (omit for non-route headings)
 *   requiredPermission : optional gate; if present, the item only shows
 *                        for users whose /auth/me permissions[] includes it
 *   requiredRole       : legacy gate during the WI-007/WI-008 transition
 *                        — uses the derived legacy Role from AuthContext.
 *                        Set when no permission key fits cleanly yet.
 *   children           : nested sub-nav (one level deep only)
 *
 * Rendering order is the array order. Sections are separated by null items
 * with kind:'divider' so we can group nav visually.
 */

import type { LucideIcon } from 'lucide-react'
import {
  Home, Map, Users, MessageSquare, ShieldCheck,
  Pill, ScrollText, Stethoscope, Bell,
  HeartPulse, BarChart3, FileBarChart, LayoutDashboard, Settings, KeyRound,
  Building2, Truck, Globe, ClipboardPlus, Calculator, Mail,
  LifeBuoy, Inbox, Terminal, Share2, Activity, Wrench, Receipt, ListChecks, Bot } from 'lucide-react'

// D-SOC-9 S1: 'facility' added. The canonical list is RoleBucket in auth/roleBuckets.ts, which
// has carried 'facility' and 'manufacturer' since those portals shipped; this local copy had
// neither, so a nav entry for them was literally not expressible — which is one reason the
// facility portal had no sidebar. 'manufacturer' is deliberately still absent: that portal
// renders outside the shell entirely (D-MFR-1), so it has no nav entries to type.
export type Role = 'pharmacy_staff' | 'provider' | 'provider_nurse' | 'patient' | 'admin' | 'support' | 'facility'

/**
 * UI mode an item should appear in. WI-893 SaaS-vs-pharmacy split.
 *   'saas'     — only when in SaaS mode (admin browsing across tenants)
 *   'pharmacy' — only when in pharmacy mode (operating on one pharmacy)
 *   'both'     — visible in both modes (e.g. Roadmap, Sign out)
 * Default if omitted: 'pharmacy' (legacy behavior — most items pre-WI-893
 *                    were pharmacy-scoped operations).
 */
export type ItemMode = 'saas' | 'pharmacy' | 'both'

export interface NavItem {
  kind?: 'item'
  key: string
  label: string
  icon: LucideIcon
  route?: string
  requiredPermission?: string
  requiredRole?: Role | Role[]
  /** ADR-019 D-9: opt-in module key (e.g. 'carerx'); item hidden if the pharmacy isn't entitled. */
  requiredModule?: string
  /**
   * D-POT-2: a per-pharmacy SWITCH (CareRx sub-module such as 'carerx_pot_classic') that HIDES the
   * item when known-off — unlike `requiredModule`, which keeps an unlicensed item visible but locked.
   * A switched-off experience is a choice the pharmacy made, not something to upsell.
   */
  hiddenWhenModuleDisabled?: string
  /** Which UI mode this item should appear in. Default: 'pharmacy'. */
  mode?: ItemMode
  /** For a `?tab=` route: the other tab keys that also mark this item active (its sub-tabs). */
  matchTabs?: string[]
  children?: NavItem[]
}

export interface NavDivider {
  kind: 'divider'
  key: string
  label?: string
  /** Mode-gate dividers too. Default: 'both'. */
  mode?: ItemMode
}

export type NavEntry = NavItem | NavDivider

// ------------------------------------------------------------------
// THE NAV
// ------------------------------------------------------------------
//
// Permission strings come from the seeded permissions catalog (016).
// Items without a requiredPermission show for every authenticated user.
// ------------------------------------------------------------------

export const navConfig: NavEntry[] = [
  // ===================================================================
  // BOTH modes
  // ===================================================================
  { kind: 'item', key: 'home', label: 'Home', icon: Home, route: '/', mode: 'both' },
  { kind: 'item', key: 'notifications', label: 'Notifications', icon: Bell,
    route: '/notifications', mode: 'both',
  },

  // ===================================================================
  // SaaS mode (Owl admin, no active pharmacy)
  // ===================================================================
  { kind: 'divider', key: 'd-saas-tenants', label: 'Tenants', mode: 'saas' },
  { kind: 'item', key: 'saas-pharmacies', label: 'Pharmacies', icon: Building2,
    route: '/admin/pharmacies', requiredPermission: 'admin.users.read', mode: 'saas',
  },
  { kind: 'item', key: 'saas-users', label: 'Users (all tenants)', icon: Users,
    route: '/admin/users', requiredPermission: 'admin.users.read', mode: 'saas',
  },

  // --- Monitoring (platform health & observability) ---
  // D-ADMIN-IA-1: consolidated the old "Support & monitoring" + "Platform"
  // grab-bags into intent-named buckets. "Portal settings" (this SaaS block) is
  // now cleanly separated from "Pharmacy settings" (the pharmacy-mode block below).
  { kind: 'divider', key: 'd-saas-monitoring', label: 'Monitoring', mode: 'saas' },
  { kind: 'item', key: 'saas-system', label: 'Platform overview', icon: LayoutDashboard,
    route: '/admin', requiredRole: ['admin', 'support'], mode: 'saas',
  },
  { kind: 'item', key: 'saas-logs', label: 'System logs', icon: Terminal,
    route: '/admin/logs', requiredPermission: 'support.logs.read', mode: 'saas',
  },
  // D-SUPPORT-3 R3: one-page service health for the support tier (admin sees it too).
  { kind: 'item', key: 'saas-support-monitor', label: 'Monitoring', icon: Activity,
    route: '/admin/support/monitor', requiredPermission: 'support.queue.read', mode: 'saas',
  },
  // D-OBS-1: per-pharmacy Latency board (P3 shipped).
  { kind: 'item', key: 'saas-latency', label: 'Latency', icon: Activity,
    route: '/admin/latency', requiredRole: ['admin', 'support'], mode: 'saas',
  },

  // --- Portal configuration (platform-wide config, all tenants) ---
  { kind: 'divider', key: 'd-saas-config', label: 'Portal configuration', mode: 'saas' },
  { kind: 'item', key: 'saas-sso', label: 'Portal SSO', icon: ShieldCheck,
    route: '/admin/sso', requiredRole: 'admin', mode: 'saas',
  },
  { kind: 'item', key: 'saas-email', label: 'Portal email', icon: Mail,
    route: '/admin/email', requiredRole: 'admin', mode: 'saas',
  },
  { kind: 'item', key: 'saas-api-keys', label: 'API keys', icon: KeyRound,
    route: '/admin/api-keys', requiredPermission: 'account.api_keys.self', mode: 'saas',
  },

  // --- Support & feedback ---
  { kind: 'divider', key: 'd-saas-support', label: 'Support & feedback', mode: 'saas' },
  // D-SUPPORT-8: /admin/support/home had NO nav entry — its only route in was the post-login
  // redirect for role 'support', so an ADMIN could never reach it from the UI. That page is where
  // each customer's primary/secondary technician is set, and under the ownership policy the
  // technician is what makes someone the notified owner of a case. The surface that decides who
  // gets told about a customer's cases must not be reachable only by typing a URL.
  { kind: 'item', key: 'saas-support-customers', label: 'Customers', icon: Building2,
    route: '/admin/support/home', requiredPermission: 'support.queue.read', mode: 'saas',
  },
  { kind: 'item', key: 'saas-support', label: 'Support queue', icon: Inbox,
    route: '/admin/support', requiredPermission: 'support.queue.read', mode: 'saas',
  },
  // D-SUPPORT-3: the agent's own open work, one click away, badge-counted like the queue.
  { kind: 'item', key: 'saas-support-mine', label: 'My queue', icon: Inbox,
    route: '/admin/support?tab=mine', requiredPermission: 'support.queue.read', mode: 'saas',
  },
  // D-SUPPORT-4 W5: FRT/TTR/load per agent+customer — supervisor tier and up.
  { kind: 'item', key: 'saas-support-team', label: 'Team performance', icon: BarChart3,
    route: '/admin/support/team', requiredPermission: 'support.tickets.manage', mode: 'saas',
  },
  // D-SUPPORT-4: member profiles, sessions, coverage, hours — supervisor+ (edits tiered inside).
  { kind: 'item', key: 'saas-support-members', label: 'Team', icon: Users,
    route: '/admin/support/members', requiredPermission: 'support.tickets.manage', mode: 'saas',
  },
  // D-SUPPORT-15 (Patrick 2026-08-22): "this is going to be our placeholder for all tier 1 support
  // tools." ONE nav row that holds many tools, rather than a row per tool — Support & feedback
  // already carries eight entries, and a sidebar that grows with every diagnostic stops being
  // navigation. Notification delivery is the first tool; the rest go in the registry inside
  // SupportToolsPage, not here.
  // ALL support tiers, agents included (Patrick 2026-08-24: "support tools not showing for
  // support agent user, this need to be available for all"). It was gated on
  // support.tickets.manage, which is supervisor-and-above, so the one tier that spends all day
  // in the queue could not reach the tools built for working it. Individual tools still declare
  // their own permission, so opening the door does not widen what is behind it.
  { kind: 'item', key: 'saas-support-tools', label: 'Support tools', icon: Wrench,
    route: '/admin/support/tools', requiredPermission: 'support.tickets.work', mode: 'saas',
  },
  { kind: 'item', key: 'saas-feedback', label: 'Feedback', icon: MessageSquare,
    route: '/admin/feedback', requiredPermission: 'admin.feedback.read', mode: 'saas',
  },
  { kind: 'item', key: 'saas-audit', label: 'Audit log (system-wide)', icon: ScrollText,
    route: '/admin/audit', requiredPermission: 'admin.audit.read', mode: 'saas',
  },
  { kind: 'item', key: 'roadmap', label: 'Roadmap', icon: Map,
    route: '/roadmap', requiredPermission: 'platform.access', mode: 'saas',
  },

  // ===================================================================
  // Pharmacy mode (operating on one specific pharmacy)
  // ===================================================================

  // --- Dashboards ---
  { kind: 'divider', key: 'd-insights', label: 'Insights', mode: 'pharmacy' },
  { kind: 'item', key: 'dash-ops', label: 'Operations', icon: LayoutDashboard,
    route: '/dashboards/operations', requiredPermission: 'insights.operations', requiredModule: 'insights', mode: 'pharmacy',
  },
  { kind: 'item', key: 'dash-clinical', label: 'Clinical', icon: HeartPulse,
    route: '/dashboards/clinical', requiredPermission: 'insights.clinical', requiredModule: 'insights', mode: 'pharmacy',
  },
  { kind: 'item', key: 'dash-analytics', label: 'Analytics', icon: BarChart3,
    route: '/dashboards/insights', requiredPermission: 'insights.analytics', requiredModule: 'insights', mode: 'pharmacy',
  },
  { kind: 'item', key: 'reports', label: 'Reports', icon: BarChart3,
    route: '/reports', requiredPermission: 'insights.reports', requiredModule: 'insights', mode: 'pharmacy',
  },
  { kind: 'item', key: 'published-reports', label: 'Published Reports', icon: FileBarChart,
    route: '/published-reports', requiredPermission: 'insights.published', requiredModule: 'insights', mode: 'pharmacy',
  },

  // --- Day-to-day records ---
  { kind: 'divider', key: 'd-records', label: 'Records', mode: 'pharmacy' },
  { kind: 'item', key: 'rx', label: 'Prescriptions', icon: Pill,
    route: '/rx', requiredPermission: 'rx.read', requiredRole: ['pharmacy_staff','admin'], mode: 'pharmacy',
  },
  { kind: 'item', key: 'patients', label: 'Patients', icon: Users,
    route: '/patients', requiredPermission: 'rx.read', requiredRole: ['pharmacy_staff','admin'], mode: 'pharmacy',
  },
  // Secure Share — provider/nurse document intake. Module-gated (per-pharmacy opt-in); hidden until enabled.
  { kind: 'item', key: 'secure-share', label: 'Secure Share', icon: Share2,
    route: '/secure-share', requiredPermission: 'patient.docs.read', requiredModule: 'secure_share', mode: 'pharmacy',
  },
  { kind: 'item', key: 'prescribers', label: 'Prescribers', icon: Stethoscope,
    route: '/prescribers', requiredPermission: 'rx.read', requiredRole: ['pharmacy_staff','admin'], mode: 'pharmacy',  },
  { kind: 'item', key: 'pharmacy-support', label: 'Support', icon: LifeBuoy,
    route: '/support', requiredPermission: 'support.tickets.read_own', mode: 'pharmacy',  },

  // --- CareRx & clinical tools (I-980) — own section ---
  { kind: 'divider', key: 'd-carerx', label: 'CareRx', mode: 'pharmacy' },
  // CareRx — native clinical documentation module (Care Plan / POT, enrollment, PCW,
  // labs, nurse billing).
  { kind: 'item', key: 'carerx', label: 'CareRx', icon: ClipboardPlus,
    // D-RBAC-2: module access is its own permission (carerx.access), granted per role under Roles → modules.
    route: '/carerx', requiredPermission: 'carerx.access', requiredRole: ['pharmacy_staff','admin'], mode: 'pharmacy',
    requiredModule: 'carerx',  },
  // Owner 2026-10-05: Billing & Agency, Admin and AI consent left the CareRx tab rail for the sidebar,
  // pharmacy admins only. Each child opens the same CareRx screen by its ?tab= deep link.
  { kind: 'item', key: 'carerx-admin', label: 'CareRx admin', icon: Wrench,
    requiredPermission: 'carerx.access', requiredRole: 'admin', requiredModule: 'carerx', mode: 'pharmacy',
    children: [
      { kind: 'item', key: 'carerx-billing', label: 'Billing & Agency', icon: Receipt,
        route: '/carerx?tab=billing', matchTabs: ['my-visits', 'staffing', 'coverage', 'nurses'],
        requiredPermission: 'carerx.access', requiredRole: 'admin', requiredModule: 'carerx', mode: 'pharmacy' },
      { kind: 'item', key: 'carerx-protocols', label: 'Protocols & Supply', icon: ListChecks,
        route: '/carerx?tab=admin-protocols', matchTabs: ['supply'],
        requiredPermission: 'carerx.access', requiredRole: 'admin', requiredModule: 'carerx', mode: 'pharmacy' },
      { kind: 'item', key: 'carerx-ai-consent', label: 'AI consent', icon: Bot,
        route: '/carerx?tab=ai-consent',
        requiredPermission: 'carerx.access', requiredRole: 'admin', requiredModule: 'carerx', mode: 'pharmacy' },
    ] },
  // D-CRX-47 POT-8: the CareRx home-infusion nurse's own POT visits (scoped to her). Lives under the
  // CareRx section header (not Fulfillment) so a provider_nurse's sidebar plainly shows CareRx. No
  // shipments/admin. Staff/admin don't see this (it's provider_nurse-only); they get the full 'carerx' item.
  // D-POT-2 (owner 2026-10-20): My visits is the native POT experience — hidden when the pharmacy chose POT classic.
  { kind: 'item', key: 'nurse-my-visits', label: 'CareRx — My visits', icon: ClipboardPlus,
    route: '/carerx/my-visits', requiredPermission: 'carerx.access', requiredRole: ['provider_nurse'], requiredModule: 'carerx', mode: 'pharmacy',
    hiddenWhenModuleDisabled: 'carerx_pot_native',
  },
  // D-POT-2: "POT classic" — the standalone ASP POT app's screens (dashboard, 14-step Plan of
  // Treatment, enrollment, patients, labs, re-careplan) rebuilt inside OwlRx with the same layout
  // and labels so nurses moving over are not retrained. Same module/permission gate as CareRx, plus
  // the per-pharmacy choice: shown only where the pharmacy chose POT classic (exclusive with native).
  { kind: 'item', key: 'pot-classic', label: 'POT (classic)', icon: ListChecks,
    route: '/pot/dashboard', requiredPermission: 'carerx.access', requiredRole: ['pharmacy_staff', 'admin', 'provider_nurse'], requiredModule: 'carerx', mode: 'pharmacy',
    hiddenWhenModuleDisabled: 'carerx_pot_classic',
  },
  { kind: 'item', key: 'dosing-calculator', label: 'IG Dosing calculator', icon: Calculator,
    route: '/carerx/dosing-calculator', requiredPermission: 'calculator.access', requiredRole: ['pharmacy_staff','admin'],
    mode: 'pharmacy', requiredModule: 'calculator',  },

  // --- Shipments (Sprint 18) — own top-level section ---
  { kind: 'divider', key: 'd-shipping', label: 'Fulfillment', mode: 'pharmacy' },
  { kind: 'item', key: 'shipments', label: 'Shipments', icon: Truck,
    route: '/shipments', requiredPermission: 'shipping.read', requiredModule: 'shipping', mode: 'pharmacy',  },
  // Manager/admin-only (mig 334): the report aggregates carrier costs — not for line staff.
  { kind: 'item', key: 'shipping-report', label: 'Shipping report', icon: BarChart3,
    route: '/shipments/report', requiredPermission: 'shipping.report', requiredModule: 'shipping', mode: 'pharmacy',  },
  // Privileged cross-pharmacy search — only visible if you hold the permission.
  // Surfaced in nav so Shipping Admins know it exists, but RequirePerm on the
  // route gates the page itself as defense-in-depth.
  { kind: 'item', key: 'global-search', label: 'Global order search', icon: Globe,
    route: '/shipments/global-search', requiredPermission: 'shipping.global_lookup', requiredModule: 'shipping', mode: 'pharmacy',  },
  // Courier-wide volume by pharmacy (migration 335). Same cross-tenant gate as
  // global search — it reads orders belonging to every customer on the courier.
  { kind: 'item', key: 'courier-census', label: 'Courier volume report', icon: Globe,
    route: '/shipments/courier-census', requiredPermission: 'shipping.global_lookup', requiredModule: 'shipping', mode: 'pharmacy',  },

  // --- Provider (a kind of pharmacy-mode user) ---
  // Owner 2026-08-09: /provider is the practice dashboard; the Rx list moved off the home route
  // so "Home" and "My prescriptions" are no longer the same page.
  // D-POT-2 R20: prescriber-only. A provider_nurse works in POT classic / CareRx — My visits, so
  // these prescriber screens no longer appear in her sidebar (the /provider/* routes still admit her).
  { kind: 'item', key: 'provider-home', label: 'Home', icon: LayoutDashboard,
    route: '/provider', requiredRole: ['provider'], mode: 'pharmacy',
  },
  { kind: 'item', key: 'provider', label: 'My prescriptions', icon: Stethoscope,
    route: '/provider/prescriptions', requiredRole: ['provider'], mode: 'pharmacy',
  },
  { kind: 'item', key: 'provider-patients', label: 'My patients', icon: Users,
    route: '/provider/patients', requiredRole: ['provider'], mode: 'pharmacy',
  },

  // --- Facility (D-SOC-9 S1) ---
  // A contracted SNF gets the SAME shell as a provider but its OWN routes and APIs. Separate
  // entries rather than adding 'facility' to the provider items above, because the routes differ
  // (/facility/* not /provider/*). Labels match the provider portal's wording (Mario,
  // 2026-09-25: "my residents should be named my patients") — the route stays /residents so the
  // URL is not a second thing to change later.
  { kind: 'item', key: 'facility-home', label: 'Home', icon: LayoutDashboard,
    route: '/facility', requiredRole: ['facility'], mode: 'pharmacy',
  },
  { kind: 'item', key: 'facility-rx', label: 'My prescriptions', icon: Stethoscope,
    route: '/facility/prescriptions', requiredRole: ['facility'], mode: 'pharmacy',
  },
  { kind: 'item', key: 'facility-residents', label: 'My patients', icon: Users,
    route: '/facility/residents', requiredRole: ['facility'], mode: 'pharmacy',
  },

  // --- Patient (a kind of pharmacy-mode user) ---
  { kind: 'item', key: 'patient', label: 'My medications', icon: HeartPulse,
    route: '/patient', requiredRole: 'patient', mode: 'pharmacy',
  },

  // --- Pharmacy settings (combined: per-pharmacy admin + self-service) ---
  // Per sponsor request (2026-05-11): the older "Pharmacy admin" group
  // (Staff, Roles & permissions, Invitations) is folded under "Settings"
  // Per sponsor request 2026-05-12: all settings grouped under
  // My pharmacy as a tabbed hub page. The sidebar has ONE item;
  // the hub page renders Profile/Staff/Roles/Invitations/Audit/Views
  // as internal tabs gated by individual permissions.
  // --- Staff management (users + prescriber/team access). Separate from "My pharmacy"
  // so Managers (admin.users.read, no pharmacy.self.manage) can manage their team
  // without seeing pharmacy profile/credential settings. ---
  // --- Pharmacy settings (per-tenant config: this pharmacy only) ---
  // D-ADMIN-IA-1: team management (Users) grouped WITH pharmacy settings so the
  // "Pharmacy settings" bucket is one coherent area, mirroring the "Portal
  // configuration" bucket in SaaS mode. Kept as a separate nav item (not just a
  // hub tab) because Managers with admin.users.read but not pharmacy.self.manage
  // must reach their team without the pharmacy profile/credential settings.
  { kind: 'divider', key: 'd-settings', label: 'Pharmacy settings', mode: 'pharmacy' },
  { kind: 'item', key: 'settings-pharmacy', label: 'Pharmacy settings', icon: Settings,
    route: '/settings/pharmacy', requiredPermission: 'pharmacy.self.manage', mode: 'pharmacy',
  },
  // D-RBAC-1 (Patrick 2026-09-11): groups live INSIDE this page as a second tab; the item is
  // renamed "Users and groups". Roles keep their own tab under Pharmacy settings.
  { kind: 'item', key: 'staff', label: 'Users and groups', icon: Users,
    route: '/manage-users', requiredPermission: 'admin.users.read', mode: 'pharmacy',
  },

]

// ------------------------------------------------------------------
// Visibility filter
// ------------------------------------------------------------------

export interface NavVisibilityContext {
  role: Role | null
  permissions: string[]
  authenticated: boolean
  /** WI-893: current UI mode. Items not matching the current mode are filtered out. */
  mode: 'saas' | 'pharmacy'
  /**
   * ADR-019 D-9: returns true only when an opt-in module is KNOWN to be disabled
   * for this pharmacy. Unknown (still loading) => false => show (optimistic), so
   * entitled users never see a flicker. Optional so callers can omit it.
   */
  isModuleDisabled?: (moduleKey: string) => boolean
}

function itemMatchesMode(itemMode: ItemMode | undefined, ctxMode: 'saas' | 'pharmacy'): boolean {
  // Default: 'pharmacy' (legacy behavior — pre-WI-893 most items were pharmacy-scoped)
  const m = itemMode ?? 'pharmacy'
  return m === 'both' || m === ctxMode
}

function userMatchesItem(item: NavItem, ctx: NavVisibilityContext): boolean {
  if (!ctx.authenticated) return false

  // Mode gate (WI-893)
  if (!itemMatchesMode(item.mode, ctx.mode)) return false

  // requiredRole is OR-matched against current role
  if (item.requiredRole) {
    const allowed = Array.isArray(item.requiredRole) ? item.requiredRole : [item.requiredRole]
    if (!ctx.role || !allowed.includes(ctx.role)) return false
  }

  // requiredPermission is checked against the user's permission set
  if (item.requiredPermission) {
    if (!ctx.permissions.includes(item.requiredPermission)) return false
  }

  // ADR-019 D-9: module-gated items stay VISIBLE even when not entitled — the sidebar renders
  // them locked (greyed + lock) rather than hiding them, so pharmacies can see what's available.
  // (Visibility still respects role/permission/mode above.)

  // D-POT-2: a per-pharmacy switch hides, it does not lock. Unknown (still loading) => show.
  if (item.hiddenWhenModuleDisabled && ctx.isModuleDisabled?.(item.hiddenWhenModuleDisabled)) return false

  return true
}

/**
 * Walk the nav config and return only the entries the user can see.
 * Dividers are dropped if they would have no following items before
 * the next divider, and dividers also respect mode gates.
 */
export function visibleNav(entries: NavEntry[], ctx: NavVisibilityContext): NavEntry[] {
  const out: NavEntry[] = []
  let pendingDivider: NavDivider | null = null
  let sectionHadItem = false

  for (const e of entries) {
    if (e.kind === 'divider') {
      if (pendingDivider && !sectionHadItem) {
        // (drop)
      }
      // Dividers also mode-gated
      pendingDivider = itemMatchesMode(e.mode, ctx.mode) ? e : null
      sectionHadItem = false
      continue
    }
    if (!userMatchesItem(e, ctx)) continue
    // A group shows only the children this user may see, and disappears when that is none.
    let item: NavItem = e
    if (e.children) {
      const kids = e.children.filter(c => userMatchesItem(c, ctx))
      if (kids.length === 0) continue
      item = { ...e, children: kids }
    }

    if (pendingDivider) {
      out.push(pendingDivider)
      pendingDivider = null
    }
    out.push(item)
    sectionHadItem = true
  }
  return out
}

/**
 * Is `item` the current page? A plain route matches its path; a `?tab=` route matches its path AND
 * the tab (or one of its matchTabs). NavLink alone ignores the query, so every /carerx?tab=… item
 * would light up together.
 */
export function routeMatches(item: NavItem, pathname: string, search: string): boolean {
  if (!item.route) return false
  const [path, query] = item.route.split('?')
  const here = pathname.replace(/\/+$/, '') || '/'
  if (!(here === path || here.endsWith(path))) return false
  const want = new URLSearchParams(query ?? '').get('tab')
  if (!want) return true
  const tab = new URLSearchParams(search).get('tab')
  return tab === want || (!!tab && (item.matchTabs ?? []).includes(tab))
}
